/**
 * Decision fields on a workflow step (permit determination, clearance checks, revise terms, manual "signed on paper"…).
 * Submitting saves the answers to the workflow and closes the step's task; the workflow moves on.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { post } from "../../lib/api";
import { ErrorNote } from "../ui";

export interface DecisionField { key: string; label: string; type: "text" | "textarea" | "select" | "date" | "checkbox"; required?: boolean; options?: string[]; help?: string }

export function StepForm({ instanceId, stepKey, fields, onDone }: { instanceId: string; stepKey: string; fields: DecisionField[]; onDone?: () => void }) {
  const qc = useQueryClient();
  const [v, setV] = useState<Record<string, unknown>>({});
  const [miss, setMiss] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => post(`/api/workflows/instances/${instanceId}/steps/${stepKey}/decision`, { values: v }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["wf", instanceId] }); void qc.invalidateQueries({ queryKey: ["wf-inst"] }); onDone?.(); },
  });
  const submit = () => {
    const missing = fields.find((f) => f.required && (f.type === "checkbox" ? v[f.key] !== true : !String(v[f.key] ?? "").trim()));
    if (missing) { setMiss(missing.key); return; }
    setMiss(null);
    save.mutate();
  };
  const set = (k: string, x: unknown) => { setV((cur) => ({ ...cur, [k]: x })); if (miss === k) setMiss(null); };
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-[var(--line)] bg-[var(--panel-2)] p-3 text-sm">
      {fields.map((f) => (
        <label key={f.key} className={f.type === "checkbox" ? "flex items-start gap-2" : "block space-y-1"}>
          {f.type === "checkbox" ? (
            <>
              <input type="checkbox" className="mt-0.5 size-4 shrink-0" checked={v[f.key] === true} onChange={(e) => set(f.key, e.target.checked)} />
              <span>{f.label}{f.required && <span className="text-red-600"> *</span>}</span>
            </>
          ) : (
            <>
              <span className="text-xs font-medium">{f.label}{f.required && <span className="text-red-600"> *</span>}</span>
              {f.type === "select" ? (
                <select className="input !py-1 text-sm" value={String(v[f.key] ?? "")} onChange={(e) => set(f.key, e.target.value)}>
                  <option value="">Choose…</option>
                  {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === "textarea" ? (
                <textarea className="input min-h-[4rem] font-[inherit] text-sm" value={String(v[f.key] ?? "")} onChange={(e) => set(f.key, e.target.value)} maxLength={4000} />
              ) : (
                <input className="input !py-1 text-sm" type={f.type === "date" ? "date" : "text"} value={String(v[f.key] ?? "")} onChange={(e) => set(f.key, e.target.value)} maxLength={500} />
              )}
            </>
          )}
          {f.help && <span className="muted block text-xs">{f.help}</span>}
          {miss === f.key && <span className="block text-xs text-red-600">{f.type === "checkbox" ? "Confirm this first" : "Fill this in first"}</span>}
        </label>
      ))}
      <ErrorNote error={save.error} />
      <button type="button" className="btn btn-primary !min-h-0 !py-1.5 text-xs" disabled={save.isPending} onClick={submit}>{save.isPending ? "Saving…" : "Submit"}</button>
    </div>
  );
}
